import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { CheckCircle2, XCircle, Cloud, Copy, AlertCircle, RefreshCw, Loader2, ExternalLink, ShieldCheck } from 'lucide-react';
import { fmtDateTime } from '../../lib/docs';
import { toast } from 'sonner';

export default function AdminDropboxPage() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.dropboxStatus(), api.dropboxLogs(50)]);
      setStatus(s);
      setLogs(l);
    } catch { toast.error('Could not load Dropbox status'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const flag = params.get('status');
    if (flag === 'connected') toast.success('Dropbox connected');
    if (flag === 'error') toast.error(`Dropbox error: ${params.get('detail') || 'unknown'}`);
  }, [params]);

  const connect = async () => {
    try {
      setBusy(true);
      const { url } = await api.dropboxAuthUrl();
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start OAuth flow');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect Dropbox? Existing files stay accessible via stored paths but new uploads will go to local storage.')) return;
    try {
      setBusy(true);
      await api.dropboxDisconnect();
      toast.success('Dropbox disconnected');
      await load();
    } catch { toast.error('Could not disconnect'); }
    finally { setBusy(false); }
  };

  const setActive = async (provider) => {
    try {
      await api.dropboxSetActive(provider);
      toast.success(`Active storage set to ${provider}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not set provider'); }
  };

  const verify = async () => {
    try {
      setBusy(true);
      const res = await api.dropboxVerify();
      if (res.ok) {
        toast.success(`Round-trip OK · ${res.size}B written to Dropbox and deleted`);
      } else {
        toast.error('Round-trip mismatch — uploaded and downloaded bytes differ');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  const copyRedirect = () => {
    navigator.clipboard.writeText(status?.redirect_uri || '');
    toast.success('Redirect URI copied');
  };

  return (
    <AdminLayout title="Dropbox Storage" subtitle="Sync the secure document vault to Dropbox.">
      {loading ? <div className="py-20 text-center text-neutral-500">Loading…</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-neutral-200 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Connection</p>
                  <p className="text-[22px] font-extrabold text-[#14110f] flex items-center gap-2">
                    {status?.connected
                      ? <><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Connected{status.healthy ? '' : ' (unhealthy)'}</>
                      : <><XCircle className="w-5 h-5 text-rose-500" /> Not connected</>}
                  </p>
                  {status?.account?.email && (
                    <p className="text-[12.5px] text-neutral-500 mt-1">
                      {status.account.name} · {status.account.email}
                    </p>
                  )}
                  {status?.connected_at && (
                    <p className="text-[11.5px] text-neutral-400 mt-0.5">Connected {fmtDateTime(status.connected_at)}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {!status?.connected ? (
                    <button data-testid="dbx-connect" onClick={connect} disabled={busy}
                            className="inline-flex items-center gap-2 bg-[#0061FF] hover:bg-[#004ee0] text-white font-semibold px-5 py-2.5 rounded-md text-[13px] disabled:opacity-50">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />} Connect Dropbox
                    </button>
                  ) : (
                    <>
                      <button data-testid="dbx-verify" onClick={verify} disabled={busy}
                              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2.5 rounded-md text-[13px] disabled:opacity-50">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Verify connection
                      </button>
                      <button onClick={connect} disabled={busy}
                              className="inline-flex items-center gap-2 border border-neutral-200 hover:bg-neutral-50 text-[#14110f] font-semibold px-4 py-2.5 rounded-md text-[13px]">
                        <RefreshCw className="w-3.5 h-3.5" /> Reconnect
                      </button>
                      <button data-testid="dbx-disconnect" onClick={disconnect} disabled={busy}
                              className="inline-flex items-center gap-2 border border-rose-100 text-rose-600 hover:bg-rose-50 font-semibold px-4 py-2.5 rounded-md text-[13px]">
                        <XCircle className="w-3.5 h-3.5" /> Disconnect
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-5 p-3.5 rounded-md bg-[#fbf7ee] text-[12.5px] text-neutral-700">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Whitelist this redirect URI in your Dropbox app console</p>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-[12px] break-all">{status?.redirect_uri}</code>
                  <button onClick={copyRedirect} className="text-[11px] text-[#c89200] hover:underline inline-flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer"
                   className="text-[11.5px] text-[#c89200] hover:underline mt-2 inline-flex items-center gap-1">
                  Open Dropbox App Console <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-neutral-200 p-6">
              <h3 className="text-[15px] font-bold text-[#14110f] mb-3">Active storage provider</h3>
              <p className="text-[13px] text-neutral-600 mb-4">Choose where <em>new</em> document uploads should land. Existing files keep their original provider.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ProviderCard
                  active={status?.active_provider === 'local'}
                  title="Local filesystem"
                  desc="Files saved under /app/backend/storage_data on the server."
                  onClick={() => setActive('local')}
                  testid="provider-local"
                />
                <ProviderCard
                  active={status?.active_provider === 'dropbox'}
                  disabled={!status?.connected}
                  title="Dropbox"
                  desc="Files mirrored to /WyseTribe/Documents/{Member}/{Year}/{Category}/"
                  onClick={() => setActive('dropbox')}
                  testid="provider-dropbox"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
                <h3 className="text-[15px] font-bold text-[#14110f]">Sync activity</h3>
                <button onClick={load} className="text-[12px] text-[#c89200] hover:underline inline-flex items-center gap-1">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
              </div>
              {logs.length === 0 ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">No Dropbox sync events yet.</p>
                : <table className="w-full text-[12.5px]" data-testid="dbx-logs-table">
                    <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-600">
                      <tr><th className="text-left px-5 py-2">When</th><th className="text-left px-5 py-2">Op</th><th className="text-left px-5 py-2">Path</th><th className="text-left px-5 py-2">Status</th></tr>
                    </thead>
                    <tbody>
                      {logs.map((l, i) => (
                        <tr key={i} className="border-t border-neutral-100">
                          <td className="px-5 py-2 text-neutral-500">{fmtDateTime(l.at)}</td>
                          <td className="px-5 py-2 text-neutral-700">{l.operation}</td>
                          <td className="px-5 py-2 font-mono text-[11px] text-neutral-500 truncate max-w-[280px]">{l.path}</td>
                          <td className="px-5 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${
                              l.status === 'success' ? 'bg-emerald-50 text-emerald-700' :
                              l.status === 'auth_error' ? 'bg-rose-50 text-rose-700' :
                              'bg-amber-50 text-amber-700'
                            }`}>{l.status}</span>
                            {l.error && <p className="text-[10.5px] text-rose-600 mt-1">{l.error}</p>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>}
            </div>
          </div>

          <aside className="space-y-5">
            <div className="bg-white rounded-xl border border-neutral-200 p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-3">Setup checklist</h3>
              <ol className="space-y-2.5 text-[12.5px] text-neutral-700">
                <li className="flex gap-2"><span className="text-[#c89200] font-bold">1.</span> Open <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-[#c89200] hover:underline">Dropbox App Console</a>.</li>
                <li className="flex gap-2"><span className="text-[#c89200] font-bold">2.</span> Under the app's <strong>Permissions</strong> tab, enable <code className="font-mono text-[11px]">files.content.write</code>, <code className="font-mono text-[11px]">files.content.read</code>, <code className="font-mono text-[11px]">files.metadata.read</code>.</li>
                <li className="flex gap-2"><span className="text-[#c89200] font-bold">3.</span> Under <strong>Settings</strong>, add the redirect URI shown on the left.</li>
                <li className="flex gap-2"><span className="text-[#c89200] font-bold">4.</span> Click <strong>Connect Dropbox</strong> and approve.</li>
                <li className="flex gap-2"><span className="text-[#c89200] font-bold">5.</span> Switch the active provider to Dropbox.</li>
              </ol>
            </div>
            <div className="bg-[#fbf7ee] rounded-xl border border-neutral-200 p-5">
              <AlertCircle className="w-5 h-5 text-amber-600 mb-2" />
              <h3 className="text-[13.5px] font-bold text-[#14110f]">Security</h3>
              <p className="text-[12px] text-neutral-700 mt-1">The refresh token is stored server-side only. Members and specialists never see Dropbox links — all downloads flow through our token-gated <code className="font-mono text-[11px]">/api/files/&#123;token&#125;</code> endpoint.</p>
            </div>
          </aside>
        </div>
      )}
    </AdminLayout>
  );
}

function ProviderCard({ active, disabled, title, desc, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`text-left rounded-lg border p-4 transition ${
              active ? 'border-[#f5b300] bg-[#fbf7ee]' : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
            } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[14px] font-bold text-[#14110f]">{title}</p>
        {active && <span className="text-[10.5px] font-bold text-[#c89200] uppercase tracking-wider">Active</span>}
      </div>
      <p className="text-[12px] text-neutral-600">{desc}</p>
    </button>
  );
}
